import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { Badge } from "react-bootstrap";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import "../HelpdeskScreen.css";
import { Store } from "../Store";
import { ReactComponent as VisibilityIcon } from "../assets/icons/visibility.svg";
import ChatClients from "../components/ChatClients";
import ChatHeader from "../components/ChatHeader";
import ChatMessageInput from "../components/ChatMessageInput";
import useAxios from "../hooks/useAxios";
import useCompartments from "../hooks/useCompartments";
import useProductsAndCategories from "../hooks/useProductsAndCategories";
import useSampleProductsAndCategories from "../hooks/useSampleProductsAndCategories";
import { fixScrollToBottom, getLocalTimeFormatted } from "../utils";

const reducer = (state, action) => {
  switch (action.type) {
    case "PLAY_DING":
      return { ...state, ding: true };
    case "STOP_DING":
      return { ...state, ding: false };
    default:
      return state;
  }
};

export default function HelpDeskScreen({
  chatScreenVisable,
  setChatScreenVisable,
}) {
  const axios = useAxios();
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo, messages, clients } = state;

  // userinfo.membership.businessOwner
  // userinfo._id and userinfo.name
  // userinfo.token

  const [message, setMessage] = useState("");
  const [volumeOn, setVolumeOn] = useState(false);
  const [recipient, setRecipient] = useState();
  const [showUsers, setShowUsers] = useState(true);
  const ref = useRef(null);
  const [socket, setSocket] = useState(null);
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });

  const [, , setLoadProducts, setLoadCategories, setLoadLeafCategories] =
    useProductsAndCategories();

  const [, , setLoadProducts2, setLoadCategories2, setLoadLeafCategories2] =
    useSampleProductsAndCategories();
  const [, , setLoadCompartments] = useCompartments();

  const [{ ding }, dispatch] = useReducer(reducer, {
    ding: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (userInfo) {
      const domain = window.location.origin;
      const socket = io.connect(domain);
      setSocket(socket);
      return () => {
        socket.disconnect();
      };
    }
  }, [userInfo]);

  useEffect(() => {
    if (chatScreenVisable) {
      if (recipient) {
        readMessagesHandler(recipient);

        const messageIds = messages
          .filter((m) => !m.read && m.user.id === recipient.id)
          .map((m) => m.message.id);
        axios.put(`/api/messages/markasread`, {
          messageIds,
        });
      }
    }
  }, [chatScreenVisable]);

  const linkToClientHandler = () => {
    navigate("/category/order");
    if (recipient && userInfo?.client?._id === recipient._id) {
      ctxDispatch({
        type: "HELPDESK_DISCONNECT_CLIENT",
      });
    } else {
      ctxDispatch({
        type: "HELPDESK_DISCONNECT_CLIENT",
      });
      ctxDispatch({
        type: "HELPDESK_CONNECT_TO_CLIENT",
        payload: recipient,
      });
    }
  };

  useEffect(() => {
    setLoadCategories(true);
    setLoadProducts(true);
    setLoadCompartments(true);
    setLoadLeafCategories(true);
    setLoadCategories2(true);
    setLoadProducts2(true);
    setLoadLeafCategories2(true);
  }, [userInfo]);

  const sendMessageHandler = (e) => {
    e.preventDefault();

    if (!message) {
      return;
    }

    const mess = {
      recipient: recipient._id,
      read: false,
      message: {
        text: message,
        date: new Date(),
        id: uuidv4(),
      },

      user: {
        id: userInfo._id,
        name: userInfo.name,
        businessOwner: userInfo.membership.businessOwner,
        businessName: userInfo.businessName,
      },
    };

    ctxDispatch({ type: "ADD_MESSAGE", payload: mess });

    readMessagesHandler(recipient);

    socket.emit("send_message", { type: "NEW_MESSAGE", message: mess });

    setMessage("");
  };

  const clickOut = useCallback(
    (e) => {
      if (!ref.current.contains(e.target) && chatScreenVisable) {
        setChatScreenVisable(false);
      }
    },
    [chatScreenVisable]
  );

  useEffect(() => {
    window.addEventListener("mousedown", clickOut);

    return () => {
      window.removeEventListener("mousedown", clickOut);
    };
  }, [clickOut]);

  const pcActive = useCallback(
    (e) => {
      if (chatScreenVisable) {
        readMessagesHandler(recipient);
      }
    },
    [chatScreenVisable, recipient, messages]
  );

  useEffect(() => {
    window.addEventListener("mouseenter", pcActive);

    return () => {
      window.removeEventListener("mouseenter", pcActive);
    };
  }, [pcActive]);

  var audio = new Audio("/ding.mp3");

  useEffect(() => {
    if (ding && volumeOn) {
      audio.play();
      dispatch({ type: "STOP_DING" });
    } else if (ding) {
      dispatch({ type: "STOP_DING" });
    }
  }, [ding, volumeOn]);

  useEffect(() => {
    if (socket) {
      socket.on("helpdesk_receive_message", (data) => {
        ctxDispatch({ type: "ADD_MESSAGE", payload: data });
        dispatch({ type: "PLAY_DING" });
      });

      socket.on("helpdesk_receive_read_messages", (data) => {
        ctxDispatch({
          type: "RECIPIENT_READED_MESSAGES",
          payload: data.messages,
        });
      });

      socket.on("helpdesk_receive_client_seen", (data) => {
        ctxDispatch({
          type: "CLIENT_SEEN",
          payload: data.client,
        });
      });
    }
  }, [socket]);

  useEffect(() => {
    if (messages) {
      fixScrollToBottom("helpDeskAllMessagesPanel");
    }
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`/api/messages?hours=24`);
      if (data && data.length > 0) {
        ctxDispatch({
          type: "SET_MESSAGES",
          payload: { messages: data, userId: userInfo._id },
        });
      }
    } catch (err) {}
  };

  const fetchAllClients = async () => {
    try {
      const { data } = await axios.get(`/api/messages/clients`);
      ctxDispatch({
        type: "SET_CLIENTS",
        payload: { clients: data },
      });
    } catch (err) {}
  };

  const onloadCallback = useCallback(
    (e) => {
      if (userInfo) {
        fetchMessages();
        fetchAllClients();
      }
    },
    [fetchAllClients, fetchMessages, userInfo, axios]
  );

  useEffect(() => {
    window.addEventListener("load", onloadCallback);

    return () => {
      window.removeEventListener("load", onloadCallback);
    };
  }, [onloadCallback]);

  useEffect(() => {
    if (!messages || messages.length === 0) {
      fetchMessages();
    }
    if (!clients || clients.length === 0) {
      fetchAllClients();
    }
  }, []);

  const recipientSelectHandler = (client) => {
    if (false) {
      ctxDispatch({
        type: "HELPDESK_DISCONNECT_CLIENT",
        payload: recipient,
      });
    }

    const messageIds = messages
      .filter((m) => !m.read && m.user.id === client._id)
      .map((m) => m.message.id);
    axios.put(`/api/messages/markasread`, {
      messageIds,
    });
    setRecipient(client);

    readMessagesHandler(client);

    setShowUsers(false);
  };

  const readMessagesHandler = (recipient) => {
    const messagesReaded = messages
      .filter((m) => m.user.id === recipient._id && !m.read)
      .map((m) => m.message.id);

    if (messagesReaded.length > 0) {
      socket.emit("send_message", {
        type: "READ_MESSAGES",
        messages: messagesReaded,
        recipient: recipient._id,
      });

      ctxDispatch({
        type: "READ_MESSAGES",
        payload: { userId: recipient._id },
      });
    }
  };

  return (
    <div className="helpdesk-screen-container" ref={ref}>
      {!chatScreenVisable ? (
        <div className="helpdesk-icon-container">
          {messages?.filter((m) => !m.read && m.user.id !== userInfo._id)
            .length > 0 && (
            <div className="helpdesk-badge">
              <Badge pill bg="danger">
                {
                  messages?.filter((m) => !m.read && m.user.id !== userInfo._id)
                    .length
                }
              </Badge>
            </div>
          )}
        </div>
      ) : (
        <div
          style={
            isTabletOrMobile
              ? { width: "100%" }
              : { maxWidth: "430px", minWidth: "430px" }
          }
        >
          <ChatHeader
            volumeOn={volumeOn}
            setVolumeOn={setVolumeOn}
            setChatScreenVisable={setChatScreenVisable}
            messages={messages}
            recipient={recipient || userInfo.client}
            linkToClientHandler={linkToClientHandler}
            userInfo={userInfo}
            showUsers={showUsers}
            setShowUsers={setShowUsers}
          />
          <div className="helpdesk-chat-container">
            {showUsers ? (
              <div className="helpdesk-chat-scroll">
                <ChatClients
                  clients={clients}
                  messages={messages}
                  recipientSelectHandler={recipientSelectHandler}
                />
              </div>
            ) : (
              <>
                <div
                  id="helpDeskAllMessagesPanel"
                  className="helpdesk-chat-scroll"
                >
                  {messages
                    .filter(
                      (m) =>
                        recipient?._id === m.user.id ||
                        (m.user.businessName === "HelpDesk" &&
                          m.recipient === recipient?._id)
                    )
                    .map((m) =>
                      m.user.businessName !== "HelpDesk" ? (
                        <div
                          key={m.message.id}
                          className="message-sent helpdesk-message-container"
                        >
                          <div className="helpdesk-user-message">
                            <div>{m.message.text}</div>
                            <div className="helpdesk-message-details">
                              {getLocalTimeFormatted(m.message.date)} &nbsp;
                              {m.user.name}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          key={m.message.id}
                          className="message-received helpdesk-message-container"
                        >
                          <div className="helpdesk-client-message">
                            <div>{m.message.text}</div>
                            <div className="helpdesk-message-details">
                              {getLocalTimeFormatted(m.message.date)}&nbsp;
                              {m.user.name}
                            </div>
                          </div>
                          <div className="helpdesk-visibility-icon">
                            {m.read && (
                              <VisibilityIcon width="14" fill="#2994b2" />
                            )}
                          </div>
                        </div>
                      )
                    )}
                </div>
                <ChatMessageInput
                  message={message}
                  setMessage={setMessage}
                  sendMessageHandler={sendMessageHandler}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
